import { Fragment, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    ClickAwayListener,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    PaletteMode,
    Paper,
    Popper,
    Typography,
    useMediaQuery
} from '@mui/material';

// third-party
import { Trans } from 'react-i18next';
// project imports
import LAYOUT_CONST from 'constant';
import NavCollapse from '../NavCollapse';
import NavItem from '../NavItem';
import useConfig from 'hooks/useConfig';
import Transitions from 'ui-component/extended/Transitions';
import { dispatch, useSelector } from 'store';

// assets
import { IconChevronDown, IconChevronRight, IconMinusVertical, IconMoon, IconSun } from '@tabler/icons';
import { NavItemType } from 'types';
import { activeID, activeItem } from 'store/slices/menu';
import { find } from 'lodash';
import environment from 'environment';

// mini-menu - wrapper
const PopperStyled = styled(Popper)(({ theme }) => ({
    overflow: 'visible',
    zIndex: 1202,
    minWidth: 180,
    '&:before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        top: 5,
        left: 32,
        width: 12,
        height: 12,
        transform: 'translateY(-50%) rotate(45deg)',
        zIndex: 120,
        borderWidth: '6px',
        borderStyle: 'solid',
        borderColor: `${theme.palette.background.paper}  transparent transparent ${theme.palette.background.paper}`
    }
}));

// ==============================|| SIDEBAR MENU LIST GROUP ||============================== //

type VirtualElement = {
    getBoundingClientRect: () => ClientRect | DOMRect;
    contextElement?: Element;
};

interface NavGroupProps {
    item: NavItemType;
    lastItem: number;
    remItems: NavItemType[];
    lastItemId: string;
}

const NavGroup = ({ item, lastItem, remItems, lastItemId }: NavGroupProps) => {
    const theme = useTheme();

    const { pathname } = useLocation();
    const { drawerOpen, selectedID } = useSelector((state) => state.menu);
    const { layout, borderRadius } = useConfig();
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const [anchorEl, setAnchorEl] = useState<VirtualElement | (() => VirtualElement) | null | undefined>(null);
    const [currentItem, setCurrentItem] = useState(item);
    const [isDarkMode, setIsDarkMode] = useState(theme.palette.mode === 'dark');

    const openMini = Boolean(anchorEl);

    useEffect(() => {
        if (lastItem) {
            if (item.id === lastItemId) {
                const localItem: any = { ...item };
                const elements = remItems.map((ele: NavItemType) => ele.elements);
                localItem.children = elements.flat(1);
                setCurrentItem(localItem);
            } else {
                setCurrentItem(item);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item, lastItem, layout, matchDownMd]);

    const checkOpenForParent = (child: NavItemType[], id: string) => {
        child.forEach((ele: NavItemType) => {
            if (ele.children?.length) {
                checkOpenForParent(ele.children, currentItem.id!);
            }
            if (ele.url === pathname) {
                dispatch(activeID(id));
            }
        });
    };

    const checkSelectedOnload = (data: NavItemType) => {
        const childrens = data.children ? data.children : [];
        childrens.forEach((itemCheck: NavItemType) => {
            if (itemCheck.children?.length) {
                checkOpenForParent(itemCheck.children, currentItem.id!);
            }
            if (itemCheck.url === pathname) {
                dispatch(activeID(currentItem.id!));
            }
        });
    };

    // keep selected-menu on page load and use for horizontal menu close on change routes
    useEffect(() => {
        checkSelectedOnload(currentItem);
        if (openMini) setAnchorEl(null);
        const foundItem = (items || []).find((it) => {
            return (
                document.location.pathname
                    .toString()
                    .split('/')
                    .findIndex((id) => id === it.key) > -1
            );
        });
        if (foundItem) {
            dispatch(activeItem([foundItem.key]));
        } else {
            dispatch(activeItem([]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, currentItem]);

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLDivElement, MouseEvent> | undefined) => {
        if (!openMini) {
            setAnchorEl(event?.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const Icon = currentItem?.icon!;
    const itemIcon = currentItem?.icon ? <Icon stroke={1.5} size="20px" /> : null;

    // menu list collapse & items
    const items = currentItem.children?.map((menu) => {
        switch (menu.type) {
            case 'collapse':
                return <NavCollapse key={menu.id} menu={menu} level={1} parentId={currentItem.id!} />;
            case 'item':
                return <NavItem key={menu.id} item={menu} level={1} parentId={currentItem.id!} />;
            default:
                return (
                    <Typography key={menu.id} variant="h6" color="error" align="center">
                        Menu Items Error
                    </Typography>
                );
        }
    });

    const moreItems = remItems.map((itemRem: NavItemType, i) => (
        <Fragment key={i}>
            {itemRem.title && (
                <Typography variant="caption" sx={{ pl: 2 }}>
                    {itemRem.title}
                </Typography>
            )}
            {itemRem.elements?.map((menu) => {
                switch (menu.type) {
                    case 'collapse':
                        return <NavCollapse key={menu.id} menu={menu} level={1} parentId={currentItem.id!} />;
                    case 'item':
                        return <NavItem key={menu.id} item={menu} level={1} parentId={currentItem.id!} />;
                    default:
                        return (
                            <Typography key={menu.id} variant="h6" color="error" align="center">
                                Menu Items Error
                            </Typography>
                        );
                }
            })}
        </Fragment>
    ));

    const popperId = openMini ? `group-pop-${item.id}` : undefined;
    const { onChangeMenuType } = useConfig();
    const { onChangeMode } = useConfig();

    return (
        <>
            {layout === LAYOUT_CONST.VERTICAL_LAYOUT || (layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && matchDownMd) ? (
                <Box>
                    <Box sx={{ padding: '16px 16px 0 16px' }}>
                        {environment.FEATURES.CHALLENGES_SECTION ? (
                            <Button
                                href="/challenges"
                                variant="contained"
                                fullWidth
                                sx={{
                                    backgroundColor: 'primary.800',
                                    color: '#fff',
                                    boxShadow: 'none',
                                    borderRadius: '8px',
                                    height: '40px',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    lineHeight: '130%',
                                    marginBottom: '16px',
                                    fontFamily: environment.TITLE_FONT_FAMILY,
                                    '&:hover': {
                                        backgroundColor: '#5648B2'
                                    }
                                }}
                            >
                                <Trans>checkout_section.start_a_challenge</Trans>
                            </Button>
                        ) : null}
                        <Typography variant="h6">
                            <Trans>main_menu</Trans>
                        </Typography>
                        <Divider
                            sx={{
                                borderColor: 'secondary.200',
                                mt: 2
                            }}
                        />
                    </Box>
                    <List
                        disablePadding={!drawerOpen}
                        subheader={
                            currentItem.title &&
                            drawerOpen && (
                                <Typography
                                    variant="caption"
                                    sx={{ ...theme.typography.menuCaption, textTransform: 'capitalize' }}
                                    display="block"
                                    gutterBottom
                                >
                                    {currentItem.title}
                                    {currentItem.caption && (
                                        <Typography
                                            variant="caption"
                                            sx={{ ...theme.typography.subMenuCaption, textTransform: 'capitalize' }}
                                            display="block"
                                            gutterBottom
                                        >
                                            {currentItem.caption}
                                        </Typography>
                                    )}
                                </Typography>
                            )
                        }
                        sx={{
                            '& .MuiListItemButton-root': {
                                mb: 0.5,
                                alignItems: 'center',
                                backgroundColor: 'transparent',
                                py: 1,
                                pl: 2,
                                position: 'relative',
                                '& .MuiTypography-root': {
                                    color: theme.palette.text.primary,
                                    fontFamily: 'Onest',
                                    fontSize: '14px',
                                    fontWeight: 500
                                },
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    left: '0',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: '6px',
                                    height: '16px',
                                    borderRadius: '0 4px 4px 0',
                                    transition: 'all 0.2s ease-in-out',
                                    backgroundColor: 'transparent'
                                },
                                '&:hover': {
                                    backgroundColor: 'none',
                                    '&::before': {
                                        backgroundColor: environment.THEME_COLORS.PRIMARY
                                    },
                                    '& .MuiTypography-root, & .MuiListItemIcon-root': {
                                        color: environment.THEME_COLORS.PRIMARY
                                    }
                                },
                                '&.Mui-selected': {
                                    backgroundColor: 'none',
                                    '&::before': {
                                        backgroundColor: environment.THEME_COLORS.PRIMARY
                                    },
                                    '&:hover': {
                                        backgroundColor: 'none'
                                    },
                                    '& .MuiTypography-root, & .MuiListItemIcon-root': {
                                        color: environment.THEME_COLORS.PRIMARY
                                    }
                                }
                            },
                            '& .MuiListItemIcon-root': {
                                minWidth: '36px',
                                color: theme.palette.text.primary
                            }
                        }}
                    >
                        {items}
                    </List>
                    {environment.FEATURES.THEME_CHANGE ? (
                        <Box sx={{ padding: '0 16px' }}>
                            <Divider
                                sx={{
                                    borderColor: 'secondary.200',
                                    my: 0
                                }}
                            />
                            <List>
                                <ListItemButton
                                    onClick={() => {
                                        setIsDarkMode(!isDarkMode);
                                        onChangeMenuType(isDarkMode ? 'light' : 'dark');
                                        onChangeMode?.(isDarkMode ? 'light' : ('dark' as PaletteMode));
                                    }}
                                    sx={{
                                        mb: 0.5,
                                        alignItems: 'center',
                                        backgroundColor: 'transparent',
                                        py: 1,
                                        pl: 2,
                                        position: 'relative',
                                        '&.MuiListItemButton-root': {
                                            padding: '12px 8px !important'
                                        },
                                        '& .MuiTypography-root': {
                                            color: theme.palette.text.primary,
                                            fontFamily: 'Onest',
                                            fontSize: '14px',
                                            fontWeight: 500
                                        },
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            left: '0',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            width: '6px',
                                            height: '16px',
                                            borderRadius: '0 4px 4px 0',
                                            transition: 'all 0.2s ease-in-out',
                                            backgroundColor: 'transparent'
                                        },
                                        '&:hover': {
                                            backgroundColor: 'none',
                                            '&::before': {
                                                backgroundColor: environment.THEME_COLORS.PRIMARY
                                            },
                                            '& .MuiTypography-root, & .MuiListItemIcon-root': {
                                                color: environment.THEME_COLORS.PRIMARY
                                            }
                                        }
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: '28px', color: theme.palette.text.primary }}>
                                        {isDarkMode ? <IconSun stroke={1.5} size="20px" /> : <IconMoon stroke={1.5} size="20px" />}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Typography>
                                                {isDarkMode ? <Trans>common.light_mode</Trans> : <Trans>common.dark_mode</Trans>}
                                            </Typography>
                                        }
                                    />
                                </ListItemButton>
                            </List>
                        </Box>
                    ) : null}
                </Box>
            ) : (
                <List>
                    <ListItemButton
                        selected={selectedID === currentItem.id}
                        sx={{
                            borderRadius: `${borderRadius}px`,
                            p: 1,
                            my: 0.5,
                            mr: 1,
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: 'inherit'
                        }}
                        onMouseEnter={handleClick}
                        onClick={handleClick}
                        onMouseLeave={handleClose}
                        aria-describedby={popperId}
                    >
                        {itemIcon && (
                            <ListItemIcon sx={{ minWidth: 28 }}>
                                {currentItem.id === lastItemId ? <IconMinusVertical stroke={1.5} size="20px" /> : itemIcon}
                            </ListItemIcon>
                        )}
                        <ListItemText
                            sx={{ mr: 1 }}
                            primary={
                                <Typography variant={selectedID === currentItem.id ? 'h5' : 'body1'} color="inherit">
                                    {currentItem.id === lastItemId ? <Trans>more-items</Trans> : currentItem.title}
                                </Typography>
                            }
                        />
                        {openMini ? <IconChevronDown stroke={1.5} size="16px" /> : <IconChevronRight stroke={1.5} size="16px" />}

                        {anchorEl && (
                            <PopperStyled
                                id={popperId}
                                open={openMini}
                                anchorEl={anchorEl}
                                placement="bottom-start"
                                style={{
                                    zIndex: 2001
                                }}
                            >
                                {({ TransitionProps }) => (
                                    <Transitions {...TransitionProps} in={openMini}>
                                        <Paper
                                            sx={{
                                                mt: 0.5,
                                                py: 1.25,
                                                boxShadow: theme.shadows[8],
                                                backgroundImage: 'none'
                                            }}
                                        >
                                            <ClickAwayListener onClickAway={handleClose}>
                                                <Box
                                                    sx={{
                                                        maxHeight: 'calc(100vh - 170px)',
                                                        overflowY: 'auto',
                                                        '&::-webkit-scrollbar': {
                                                            opacity: 0,
                                                            width: 4,
                                                            '&:hover': {
                                                                opacity: 0.7
                                                            }
                                                        },
                                                        '&::-webkit-scrollbar-track': {
                                                            background: 'transparent'
                                                        },
                                                        '&::-webkit-scrollbar-thumb': {
                                                            background: theme.palette.divider,
                                                            borderRadius: 4
                                                        }
                                                    }}
                                                >
                                                    {currentItem.id !== lastItemId ? items : moreItems}
                                                </Box>
                                            </ClickAwayListener>
                                        </Paper>
                                    </Transitions>
                                )}
                            </PopperStyled>
                        )}
                    </ListItemButton>
                </List>
            )}
        </>
    );
};

export default NavGroup;
